import React, { useState } from 'react'
import type { PresentationIndustry, QuoteFile, PresentationLibrary } from 'paintscout'
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ConfirmationDialog,
  Grid,
  InputField,
  Spinner,
  Tab,
  Tabs,
  Tooltip,
  Typography,
  useMediaQuery,
  makeStyles,
  PresentationFilePreview as FilePreview
} from '@paintscout/ui'
import {
  FileDropArea,
  getYouTubeId,
  useClientOptions,
  useDialogs,
  useFileDropArea,
  useUser
} from '@paintscout/ui-business'
import type { DialogProps, MuiTheme as Theme, StyleClasses } from '@paintscout/ui'
import { CloseButton } from '../../../common'
import { COMPANY_LOGO_FILE_KEY, PresentationFilesGrid, StockPhotoPanel } from '../../components'
import PublishIcon from '@material-ui/icons/Publish'
import InfoOutlinedIcon from '@material-ui/icons/InfoOutlined'
import {
  addFilesToLibrary,
  getFeature,
  getPresentationLogo,
  removeFileFromLibrary,
  getLibraryFileKey,
  selectNewLibraryFiles,
  uuid
} from '@paintscout/util/builder'
import { getSelectedFiles, getSelectedIndexes, getSelectionKey, toggleLibrarySelection } from './librarySelection'
import { getUploadedFiles } from '../../util/uploadedFiles'
import { PRESENTATION_LABEL_PLURAL, PRESENTATION_LABEL } from '@paintscout/util'
import { useFlags } from 'shared/components/common/FlagsProvider'
import { useQuote } from '../../../context'
import classnames from 'classnames'
import * as Sentry from '@sentry/core'
import { useSnackbar } from 'notistack'

type LibraryTab = 'library' | 'pexels' | 'youtube'

const useStyles = makeStyles<Theme, PresentationLibraryDialogProps>(
  (theme) => ({
    root: {},
    paper: {
      height: '100vh',
      position: 'relative'
    },
    tooltipRoot: {
      display: 'inline-block',
      marginLeft: theme.spacing(3)
    },
    tooltipBadge: {
      cursor: 'pointer',
      top: 0,
      right: 0,
      '& svg': {
        fontSize: '1rem',
        color: theme.palette.grey['700']
      }
    },
    tabsRoot: {
      borderBottom: `1px solid ${theme.palette.divider}`,
      padding: theme.spacing(0, 2),
      alignItems: 'flex-end',
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 2.75)
      }
    },
    pexelsContent: {
      display: 'flex',
      flexDirection: 'column',
      height: '100%',
      overflow: 'hidden'
    },
    preContent: {
      padding: theme.spacing(0, 4, 1),
      [theme.breakpoints.down('sm')]: {
        padding: theme.spacing(0, 3, 1)
      }
    },
    youtubeButton: {
      marginTop: theme.spacing(2)
    },
    dropAreaRootEmpty: {
      height: '100%'
    },
    dropAreaRoot: {
      minHeight: '100%'
    },
    filesGridRoot: {
      marginTop: -7
    },
    noResultsBlock: {
      alignItems: 'center',
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      height: '100%',
      justifyContent: 'center'
    },
    savingOverlay: {
      alignItems: 'center',
      background: 'rgba(255,255,255,0.8)',
      bottom: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(2),
      justifyContent: 'center',
      left: 0,
      position: 'absolute',
      right: 0,
      top: 0,
      zIndex: 10
    }
  }),
  {
    name: 'PresentationLibraryDialog'
  }
)

export interface PresentationLibraryDialogProps extends DialogProps {
  classes?: DialogProps['classes'] & StyleClasses<typeof useStyles>
  presentationLibrary: {
    addFilesToLibrary: (args: { files: QuoteFile[] }) => void
    library: PresentationLibrary
    saveLibrary: (args: { library: PresentationLibrary }) => void | Promise<void>
  }
  handleSelect?: (newFile: QuoteFile) => void
  /**
   * Multi-select mode (#23709). When set, a library tile click toggles selection instead of
   * selecting-and-closing, a footer button confirms the whole pick, and an upload hands over
   * *every* uploaded file rather than only the first. Takes precedence over `handleSelect` — a
   * caller that passes both (a gallery slot, which fills itself from the first file and appends
   * the rest) only ever hears from this one. Only the Gallery block passes it, so every other
   * field keeps single-pick behaviour.
   */
  handleSelectMultiple?: (newFiles: QuoteFile[]) => void
  onCancel: (event?: React.MouseEvent<HTMLElement>) => void
  /** Industry (or other seed) used for the initial Pexels search. */
  presentationIndustry?: PresentationIndustry
  /** When re-opening for an existing stock photo, the query that originally found it. */
  specificQuery?: string
  /** Whether the field this dialog is filling allows a YouTube video (default true). */
  allowYoutube?: boolean
}

export default function PresentationLibraryDialog(props: PresentationLibraryDialogProps) {
  const classes = useStyles(props)
  const {
    onCancel,
    handleSelect,
    handleSelectMultiple,
    presentationLibrary,
    presentationIndustry,
    specificQuery,
    allowYoutube,
    ...baseDialogProps
  } = props
  const { library: initialLibrary, saveLibrary } = presentationLibrary
  const [library, setLibrary] = useState(initialLibrary)
  const [isDirty, setIsDirty] = useState(false)
  const [tab, setTab] = useState<LibraryTab>('library')
  const [youtubeUrl, setYoutubeUrl] = useState('')
  // Multi-select is tracked by library key rather than grid index, so removing or reordering a
  // file inside the dialog can't silently shift what is selected onto a different image.
  const [selectedKeys, setSelectedKeys] = useState<string[]>([])
  const { openDialog, dismissDialog } = useDialogs()
  const { enqueueSnackbar } = useSnackbar()
  const { s3MediaUpload } = useFlags()
  const { options } = useClientOptions()
  const { quote } = useQuote()
  const { isSuperadmin } = useUser()
  const hasStockPhotos = getFeature({ options, path: 'presentation.stockPhotos' }) || isSuperadmin
  const columns = useMediaQuery('md') ? 4 : 6

  const files = library?.order.map((key) => {
    return library.values[key].file
  })
  const hasFiles = files?.length > 0

  const isMultiSelect = !!handleSelectMultiple
  // Whether this dialog has somewhere to put a pick at all, as opposed to being opened purely to
  // manage the library (the sidebar's Photos rail).
  const canFillSlot = !!handleSelect || isMultiSelect

  // A YouTube video can't be added to the library (there's nothing to store — see
  // shouldAddToLibrary), so this tab only makes sense when there's a slot to fill directly.
  const showYoutubeTab = canFillSlot && allowYoutube !== false
  const youtubeId = getYouTubeId(youtubeUrl)

  /**
   * The one place a pick leaves this dialog. `handleSelectMultiple` wins when both callbacks are
   * given, so a caller never receives the same pick twice through two contracts.
   */
  const emitSelection = (newFiles: QuoteFile[]) => {
    if (!newFiles.length) return
    if (handleSelectMultiple) {
      handleSelectMultiple(newFiles)
      return
    }
    handleSelect(newFiles[0])
  }

  const handleSelectYoutube = () => {
    if (!youtubeId) return
    emitSelection([{ key: uuid(), format: 'youtube', youtubeId, visibility: 'visible' }])
    closeAndSaveIfDirty()
  }

  // The company logo is always shown first — a live lookup (same resolver the Logo block
  // itself uses), not a file persisted to the library, so it can never go stale after a
  // rebrand and there's nothing to keep in sync.
  const { logo } = getPresentationLogo({ options, quote })
  const logoFile: QuoteFile | null = logo ? { key: COMPANY_LOGO_FILE_KEY, src: logo, visibility: 'visible' } : null

  // Prepended into the grid so it renders as that grid's own first tile rather than a
  // separate row — the offset below translates the grid's indices (which include it) back
  // to real indices into `files`/`library` for delete/select, and it's always re-prepended
  // here rather than tracked through reorders, so it can't be dragged out of first place.
  const gridFiles = logoFile ? [logoFile, ...(files || [])] : files
  const logoOffset = logoFile ? 1 : 0

  const selectedIndexes = isMultiSelect ? getSelectedIndexes(gridFiles, selectedKeys) : undefined
  const selectedCount = selectedIndexes?.length ?? 0

  const handleToggleSelection = (file: QuoteFile) => {
    setSelectedKeys((keys) => toggleLibrarySelection(keys, getSelectionKey(file)))
  }

  const handleSelectLogo = () => {
    if (!canFillSlot || isUploading) return
    emitSelection([{ ...logoFile, key: uuid() }])
    closeAndSaveIfDirty()
  }

  const handleGridFileClick = (index: number) => {
    if (isUploading) return

    if (isMultiSelect) {
      const file = gridFiles[index]
      if (file) handleToggleSelection(file)
      return
    }

    if (logoFile && index === 0) {
      handleSelectLogo()
      return
    }
    handleSelectFile(index - logoOffset)
  }

  // Confirms a multi-select pick: every selected file, in the order it was clicked, each re-keyed
  // so the gallery gets distinct tiles rather than repeats of one library entry's key.
  const handleConfirmSelection = () => {
    if (isUploading) return
    const files = getSelectedFiles(gridFiles, selectedKeys).map((file) => ({ ...file, key: uuid() }))
    if (!files.length) return
    emitSelection(files)
    closeAndSaveIfDirty()
  }

  const handleGridDelete = (index: number) => {
    if (logoFile && index === 0) return
    handleRemoveFile(index - logoOffset)
  }

  const handleGridReorder = (reorderedFiles: QuoteFile[]) => {
    handleReorderFiles(reorderedFiles.filter((file) => file !== logoFile))
  }

  // A failed library save is otherwise silent — the dialog has already closed by the time
  // it would reject, so surface it rather than losing the upload/pick with no feedback.
  const handleSaveLibraryError = (err: unknown) => {
    Sentry.captureException(err)
    enqueueSnackbar(`An error occurred while updating your ${PRESENTATION_LABEL}`, { variant: 'error' })
  }

  // With a target slot to fill (handleSelect), every new file (a single Pexels pick, or
  // one-or-more uploads) is added to the library in one save — so a multi-file drop doesn't
  // silently lose anything past the first — and only the first is selected into the slot.
  // InlineEditableMedia's own auto-add-to-library only runs for changes that *don't* come
  // through this dialog, so a file is never raced into the library by two independent saves
  // at once. The library save itself is a trivial PATCH, not worth blocking the close/showing
  // a spinner for — only the actual file upload (isUploading) is slow enough to warrant one.
  const selectNewFiles = (newFiles: QuoteFile[]) => {
    const result = selectNewLibraryFiles({ newFiles, library })
    if (!result) return
    const { selected, updatedLibrary } = result
    // In multi-select mode every new file goes to the caller, not just the one the library helper
    // nominates for a single slot (#23709).
    emitSelection(isMultiSelect ? newFiles : [selected])
    if (updatedLibrary) {
      setLibrary(updatedLibrary)
      Promise.resolve(saveLibrary({ library: updatedLibrary })).catch(handleSaveLibraryError)
    }
    onCancel()
  }

  // Without a target slot (e.g. opened from the sidebar for library management only),
  // uploads are just added to the local session library and flushed on close.
  const handleUploadFiles = (files: QuoteFile[]) => {
    if (canFillSlot && files.length) {
      // A failed upload reports the error in place of a file (useFileDropArea), so drop those
      // rather than handing a non-file to the library or the caller's slot.
      const uploaded = getUploadedFiles(files).map((file) => ({ ...file, key: uuid() }))
      if (uploaded.length) selectNewFiles(uploaded)
      return
    }
    const updatedLibrary = addFilesToLibrary({ files, library })
    if (!updatedLibrary) return
    setLibrary(updatedLibrary)
    setIsDirty(true)
  }

  const handleRemoveFile = (index: number) => {
    const file = files[index]
    openDialog(ConfirmationDialog, {
      message: `Are you sure you want to remove this item from your library? Existing ${PRESENTATION_LABEL_PLURAL} won't be affected.`,
      onConfirm: () => {
        setIsDirty(true)
        setLibrary(removeFileFromLibrary({ file, library }))
        dismissDialog()
      },
      onCancel: dismissDialog
    })
  }

  const handleReorderFiles = (files: QuoteFile[]) => {
    const newOrder = files.map(getLibraryFileKey)
    setIsDirty(true)
    setLibrary({ ...library, order: newOrder })
  }

  // Any close path — the X button, or selecting a file/photo to fill a slot — must flush a
  // dirty library first, or uploads/reorders/removals made earlier in this session (isDirty)
  // are silently lost since the dialog never saves on unmount otherwise.
  const closeAndSaveIfDirty = () => {
    if (isDirty) Promise.resolve(saveLibrary({ library })).catch(handleSaveLibraryError)
    onCancel()
  }

  const handleSelectFile = (index: number) => {
    if (isUploading) return
    handleSelect({ ...files[index], key: uuid() })
    closeAndSaveIfDirty()
  }

  // With a target slot to fill (handleSelect), a picked stock photo goes straight there, just
  // like picking an existing library item. Without one (e.g. opened from the sidebar for
  // library management only), it's added to the library instead and the tab switches back so
  // the user can see it landed.
  const handlePexelsSelect = (newFile: QuoteFile) => {
    if (isUploading) return
    if (canFillSlot) {
      selectNewFiles([newFile])
    } else {
      setIsDirty(true)
      setLibrary(addFilesToLibrary({ files: [newFile], library }))
      setTab('library')
    }
  }

  const {
    open,
    getInputProps,
    loading: isUploading
  } = useFileDropArea({
    multiple: true,
    onUpload: handleUploadFiles,
    s3MediaUploadEnabled: s3MediaUpload
  })

  const handleClose = () => {
    if (isUploading) return
    closeAndSaveIfDirty()
  }

  return (
    <Dialog {...baseDialogProps} classes={{ paper: classes.paper }} fullWidth maxWidth={'xl'}>
      <input {...getInputProps()} />
      <DialogTitle rightContent={<CloseButton onCancel={handleClose} />}>
        {hasStockPhotos ? (isMultiSelect ? 'Select Images' : 'Select an Image') : 'Library'}
        <span className={classes.tooltipRoot}>
          <Tooltip
            placement={'right'}
            content={
              tab === 'pexels'
                ? 'Search free stock photos to use in your ' + PRESENTATION_LABEL_PLURAL + '.'
                : `Add your most used images, videos, and pdfs to easily access them in any of your ${PRESENTATION_LABEL_PLURAL}.`
            }
            icon={<InfoOutlinedIcon />}
            classes={{ badge: classes.tooltipBadge }}
          />
        </span>
      </DialogTitle>
      {(hasStockPhotos || showYoutubeTab) && (
        <Tabs
          value={tab}
          onChange={(_, value: LibraryTab) => setTab(value)}
          textColor={'primary'}
          indicatorColor={'primary'}
          classes={{ root: classes.tabsRoot }}
        >
          <Tab value={'library'} label={'Library'} />
          {hasStockPhotos && <Tab value={'pexels'} label={'Pexels'} />}
          {showYoutubeTab && <Tab value={'youtube'} label={'YouTube'} />}
        </Tabs>
      )}
      {tab === 'pexels' ? (
        <DialogContent classes={{ root: classes.pexelsContent }}>
          <StockPhotoPanel
            defaultQuery={presentationIndustry}
            specificQuery={specificQuery}
            onSelect={handlePexelsSelect}
          />
        </DialogContent>
      ) : tab === 'youtube' ? (
        <DialogContent classes={{ root: classes.preContent }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <InputField
                label={'YouTube URL or ID'}
                fullWidth
                value={youtubeUrl}
                onChange={(ev) => setYoutubeUrl(ev.target.value)}
              />
              <Button
                classes={{ root: classes.youtubeButton }}
                variant={'contained'}
                color={'primary'}
                disabled={!youtubeId}
                onClick={handleSelectYoutube}
              >
                {'Use This Video'}
              </Button>
            </Grid>
            <Grid item xs={12} sm={6}>
              {youtubeId && (
                <FilePreview file={{ format: 'youtube', youtubeId, visibility: 'visible' }} square inGallery />
              )}
            </Grid>
          </Grid>
        </DialogContent>
      ) : (
        <DialogContent>
          <FileDropArea
            classes={{
              root: classnames({ [classes.dropAreaRoot]: hasFiles, [classes.dropAreaRootEmpty]: !hasFiles })
            }}
            onUpload={(files) => handleUploadFiles(files)}
            s3MediaUploadEnabled={s3MediaUpload}
          >
            {!hasFiles && !logoFile ? (
              <div className={classes.noResultsBlock}>
                <Typography variant={'overline'}>{'Your Library is empty'}</Typography>
                <Typography
                  variant={'body1'}
                >{`Add an item from your ${PRESENTATION_LABEL} or upload a file directly.`}</Typography>
                <Button startIcon={<PublishIcon />} onClick={() => open()} variant={'outlined'} disabled={isUploading}>
                  {'Upload a file'}
                </Button>
              </div>
            ) : (
              <PresentationFilesGrid
                classes={{ root: classes.filesGridRoot }}
                columns={columns}
                editable
                files={gridFiles}
                isEditable
                onDelete={handleGridDelete}
                onFileClick={canFillSlot ? handleGridFileClick : undefined}
                onReorder={handleGridReorder}
                onUploadClick={() => !isUploading && open()}
                uploadButtonPosition="start"
                selectedIndexes={selectedIndexes}
                showDetails={false}
                filePreviewProps={{ showFirstPageOnly: true, fancyHover: true }}
              />
            )}
          </FileDropArea>
        </DialogContent>
      )}
      {isMultiSelect && tab === 'library' && (
        <DialogActions>
          <Button
            variant={'contained'}
            color={'primary'}
            disabled={!selectedCount || isUploading}
            onClick={handleConfirmSelection}
          >
            {selectedCount > 1 ? `Add ${selectedCount} Items` : 'Add Item'}
          </Button>
        </DialogActions>
      )}
      {isUploading && (
        <div className={classes.savingOverlay}>
          <Spinner size={32} />
          <Typography variant={'overline'}>{'Uploading...'}</Typography>
        </div>
      )}
    </Dialog>
  )
}
